import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import cx from 'classnames';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { batch } from 'react-redux';
import { SelectOptions } from '../../../../../../../model/CommonModel';
import { ReaderError } from '../../../../../../../model/DeviceAdminModel';
import { Select } from '../../../../../../common';
import { DoorStoreSingleContext } from '../../../../DoorModal/DoorStoreSingleContext';
import { setAssuranceLevel, setCCOTZAssurance, setDefaultAssuranceLevel, setEntryReaderError, setReaderErrorType, StoreContext } from '../../../contextReader';
import styles from '../../Logic/logic.module.scss';

type Props = {
	useDoorContext: boolean;
};

const AssuranceLevels: React.FC<Props> = ({ useDoorContext }) => {
	//#region state management variables
	if (useDoorContext) {
		var {
			contextStateDoor: {
				entryReader: {
					rS485ReaderTypes,
					defaultAssuranceLevels,
					assuranceLevels,
					cCOTZAssurance,
					readerErrorType,
					readerErrorMessage,
					selections: { rS485ReaderType, defaultAssuranceLevel, assuranceLevel },
					isEntryReaderError,
				},
			},
			dispatcherDoor,
		} = useContext(DoorStoreSingleContext);
	} else {
		var {
			readerState: {
				rS485ReaderTypes,
				defaultAssuranceLevels,
				assuranceLevels,
				cCOTZAssurance,
				readerErrorType,
				readerErrorMessage,
				selections: { rS485ReaderType, defaultAssuranceLevel, assuranceLevel },
			},
			dispatcher,
		} = useContext(StoreContext);
	}

	const [assurance, setAssurance] = useState(undefined);
	const [readerName, setReaderName] = useState('');
	const [assuranceLevelsList, setAssuranceLevelsList] = useState<SelectOptions<string>[]>([]);
	const [defaultAssuranceLevelsList, setDefaultAssuranceLevelsList] = useState<SelectOptions<string>[]>([]);

	const errorMessage: JSX.Element = useMemo(() => {
		if (readerErrorType === ReaderError.Assurance || readerErrorType === ReaderError.DefaultAssuranceLevel) {
			return <span className={styles.errorMessage}>{readerErrorMessage}</span>;
		}
		return undefined;
	}, [readerErrorType]);
	//#endregion

	useEffect(() => {
		const index: number = rS485ReaderTypes.findIndex(x => x.Id === rS485ReaderType);
		if (index > -1) {
			setReaderName(rS485ReaderTypes[index].Name);
			setAssurance(assuranceLevel !== 0 ? assuranceLevel.toString() : undefined);
		}
	}, [rS485ReaderType]);

	useEffect(() => {
		let defaultAssuranceLevelOpts: SelectOptions<string>[] = defaultAssuranceLevels.map(x => ({
			label: x.Name,
			value: x.Id.toString(),
			id: x.Id.toString(),
		}));
		if (defaultAssuranceLevel < 1) {
			defaultAssuranceLevelOpts = [{ label: ' ', value: '-1', id: '-1' }, ...defaultAssuranceLevelOpts];
		}

		let assuranceLevelOpts: SelectOptions<string>[] = assuranceLevels.map(x => ({
			label: x.Name,
			value: x.Id.toString(),
			id: x.Id.toString(),
		}));
		const isAssuranceLevelOnList = assuranceLevels.find(x => x.Id === assuranceLevel) !== undefined;
		if (!isAssuranceLevelOnList || assuranceLevel < 1) {
			if (!isAssuranceLevelOnList) {
				useDoorContext ? dispatcherDoor(setAssuranceLevel(Number(0))) : dispatcher(setAssuranceLevel(Number(0)));
				setAssurance('0');
			}
			assuranceLevelOpts = [{ label: ' ', value: '0', id: '0' }, ...assuranceLevelOpts];
		}
		setDefaultAssuranceLevelsList(defaultAssuranceLevelOpts);
		setAssuranceLevelsList(assuranceLevelOpts);
	}, [defaultAssuranceLevels, assuranceLevels]);

	const handleOnChangeAssurance = (value: string) => {
		setAssurance(value);
		useDoorContext ? dispatcherDoor(setAssuranceLevel(Number(value))) : dispatcher(setAssuranceLevel(Number(value)));

		if (readerErrorType === ReaderError.Assurance) {
			useDoorContext
				? (dispatcherDoor(setReaderErrorType(undefined)), dispatcherDoor(setEntryReaderError(false)))
				: dispatcher(setReaderErrorType(undefined));
		}
	};

	const handleSetCCOTZAssurance = (value: CheckboxChangeEvent) => {
		if (!value.target.checked) {
			setAssurance(undefined);
			useDoorContext ? dispatcherDoor(setAssuranceLevel(0)) : dispatcher(setAssuranceLevel(0));
		}
		useDoorContext ? dispatcherDoor(setCCOTZAssurance(value.target.checked)) : dispatcher(setCCOTZAssurance(value.target.checked));

		if (readerErrorType === ReaderError.Assurance) {
			useDoorContext
				? (dispatcherDoor(setReaderErrorType(undefined)), dispatcherDoor(setEntryReaderError(false)))
				: dispatcher(setReaderErrorType(undefined));
		}
	};

	const handleOnChangeDefaultAssurance = (value: string) => {
		batch(() => {
			useDoorContext ? dispatcherDoor(setDefaultAssuranceLevel(Number(value))) : dispatcher(setDefaultAssuranceLevel(Number(value)));
			if (readerErrorType === ReaderError.DefaultAssuranceLevel) {
				useDoorContext
					? (dispatcherDoor(setReaderErrorType(undefined)), dispatcherDoor(setEntryReaderError(false)))
					: dispatcher(setReaderErrorType(undefined));
			}
		});
	};

	const isDefaultAssuranceLevelError =
		(isEntryReaderError && readerErrorType === ReaderError.DefaultAssuranceLevel) ||
		(!useDoorContext && readerErrorType === ReaderError.DefaultAssuranceLevel);
	const isAssuranceLevelError =
		(readerErrorType === ReaderError.Assurance && isEntryReaderError) || (readerErrorType === ReaderError.Assurance && !useDoorContext);

	return (
		<>
			<div className={styles.options}>
				<div className={styles.sectionContainer}>
					<fieldset>
						<legend className={styles.legend}>{_('AssuranceLevel')}</legend>
						<div className={styles.readerType}>
							{readerName} {_('reader')}
						</div>
						<div className={styles.assuranceSelect}>
							<div>
								<Select
									className={cx(styles.select, { [styles.error]: isDefaultAssuranceLevelError })}
									options={defaultAssuranceLevelsList}
									onChange={handleOnChangeDefaultAssurance}
									key='defaultAssurance'
									value={defaultAssuranceLevel.toString()}
									getPopupContainer={() => document.getElementById('logicReaderContainer')}
								/>
								{isDefaultAssuranceLevelError && errorMessage}
							</div>
							<span>{_('DefaultAssuranceLevel')}</span>
						</div>
						<div className={styles.ccotzAssuranceSection}>
							<div className={styles.ccotzAssurance}>
								<Checkbox checked={cCOTZAssurance} onChange={handleSetCCOTZAssurance} id={'ccotzAssurance'}>
									<span>{_('EnableLowerAssurance')}</span>
								</Checkbox>
							</div>
						</div>
						<div className={styles.assuranceSelect}>
							<div>
								<Select
									className={cx(styles.select, {
										[styles.error]: isAssuranceLevelError,
									})}
									options={assuranceLevelsList}
									onChange={handleOnChangeAssurance}
									key='assurance'
									id='readerModalAssuranceSelect'
									value={assurance}
									disabled={!cCOTZAssurance}
									getPopupContainer={() => document.getElementById('logicReaderContainer')}
								/>
								{isAssuranceLevelError && errorMessage}
							</div>
							<span>{_('CCOTZAssurance')}</span>
						</div>
					</fieldset>
				</div>
			</div>
		</>
	);
};

export { AssuranceLevels };
